import type { User } from "../../types/user";
import { StatusBadge } from "./StatusBadge";
import iconEdit from "../../assets/icon-edit.svg";
import iconBan from "../../assets/icon-ban.svg";
import iconSend from "../../assets/icon-send.svg";
import iconKey from "../../assets/icon-key.svg";

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}.${month}.${year}; ${hours}:${minutes}`;
}

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDisable: (user: User) => void;
  onResendInvite: (user: User) => void;
  onResetPassword: (user: User) => void;
}

const COLUMNS = ["Name", "Role", "Email", "Status", "Created", "Actions"] as const;

export function UserTable({
  users,
  onEdit,
  onDisable,
  onResendInvite,
  onResetPassword,
}: Readonly<UserTableProps>) {
  return (
    <div className="border border-stroke-primary rounded-xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-bg-tertiary border-b border-stroke-primary">
            {COLUMNS.map((col) => (
              <th
                key={col}
                className={`p-4 text-sm font-normal text-text-tertiary text-left ${
                  col === "Actions" ? "text-center" : ""
                }`}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} className="border-b border-stroke-primary last:border-b-0">
              <td className="px-4 py-3 text-sm text-text-primary">
                {user.name} {user.surname}
              </td>
              <td className="px-4 py-3 text-sm text-text-primary capitalize">
                {user.role}
              </td>
              <td className="px-4 py-3 text-sm text-text-primary">
                {user.email}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={user.status} />
              </td>
              <td className="px-4 py-3 text-sm text-text-primary">
                {formatDate(user.created_at)}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-2.5">
                  <button
                    onClick={() => onEdit(user)}
                    className="hover:opacity-70 transition-opacity"
                    aria-label={`Edit ${user.name} ${user.surname}`}
                  >
                    <img src={iconEdit} alt="" width={20} height={20} />
                  </button>
                  {user.status !== "disabled" && (
                    <button
                      onClick={() => onDisable(user)}
                      className="hover:opacity-70 transition-opacity text-system-red"
                      aria-label={`Disable ${user.name} ${user.surname}`}
                    >
                      <img src={iconBan} alt="" width={20} height={20} />
                    </button>
                  )}
                  {user.status === "pending" && (
                    <button
                      onClick={() => onResendInvite(user)}
                      className="hover:opacity-70 transition-opacity text-text-secondary"
                      aria-label={`Resend invite to ${user.name} ${user.surname}`}
                    >
                      <img src={iconSend} alt="" width={20} height={20} />
                    </button>
                  )}
                  {user.role === "doctor" && user.status === "active" && (
                    <button
                      onClick={() => onResetPassword(user)}
                      className="hover:opacity-70 transition-opacity text-text-secondary"
                      aria-label={`Reset password for ${user.name} ${user.surname}`}
                    >
                      <img src={iconKey} alt="" width={20} height={20} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
